import { JALALI_MONTHS_FA, MONTHS_FA } from './constants'

// ─── SMS usage helpers (tooti#316) ────────────────────────────────────────────
//
// The «مصرف پیامک» tab's pure pieces: month and purpose labels and the money
// formatting. The wire carries cost units and Rial as decimal strings so they
// stay exact; these format the strings digit by digit and never go through a
// float.

const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹'

function toFaDigits(text) {
  return String(text).replace(/[0-9]/g, d => FA_DIGITS[Number(d)])
}

/**
 * `1405-07` (shamsi) → `مهر ۱۴۰۵`; `2026-10` (gregorian) → `اکتبر ۲۰۲۶`;
 * `1405-07` (finance, Rima's 25th → 24th period) → `۲۵ شهریور تا ۲۴ مهر ۱۴۰۵`.
 */
export function smsMonthLabel(key, calendar) {
  const m = /^(\d{4})-(\d{2})$/.exec(key ?? '')
  if (!m) return key ?? ''
  if (calendar === 'finance') {
    const year = Number(m[1])
    const month = Number(m[2])
    const prev = month === 1 ? `${JALALI_MONTHS_FA[11]} ${toFaDigits(year - 1)}` : JALALI_MONTHS_FA[month - 2]
    return `۲۵ ${prev} تا ۲۴ ${JALALI_MONTHS_FA[month - 1] ?? m[2]} ${toFaDigits(m[1])}`
  }
  const names = calendar === 'gregorian' ? MONTHS_FA : JALALI_MONTHS_FA
  return `${names[Number(m[2]) - 1] ?? m[2]} ${toFaDigits(m[1])}`
}

export const SMS_PURPOSE_LABELS = {
  login: 'کد ورود',
  reminder: 'یادآوری دورهٔ رایگان',
  alert: 'هشدار به مدیر',
  contact: 'فرم تماس',
  reply: 'پاسخ پشتیبانی',
}

export function smsPurposeLabel(purpose) {
  return SMS_PURPOSE_LABELS[purpose] ?? purpose
}

/**
 * A decimal string (`144125`, `115.3`, `-2`) → Persian digits with `٬`
 * between thousands and `٫` before the fraction: `۱۴۴٬۱۲۵`, `۱۱۵٫۳`.
 * Works on the text, so a Rial sum past 2^53 keeps every digit.
 */
export function groupedFa(value) {
  if (value === null || value === undefined || value === '') return '—'
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(String(value))
  if (!m) return String(value)
  const whole = m[2].replace(/\B(?=(\d{3})+(?!\d))/g, '٬')
  return `${m[1] ? '−' : ''}${toFaDigits(whole)}${m[3] ? `٫${toFaDigits(m[3])}` : ''}`
}

/** What the email box says about a period's statement email. */
export function emailStatusText(status) {
  if (!status) return ''
  if (!status.sentAt) return 'ایمیل این دوره هنوز فرستاده نشده؛ روز ۲۵ ساعت ۹ صبح خودکار می‌رود.'
  const when = new Date(status.sentAt).toLocaleString('fa-IR', { timeZone: 'Asia/Tehran', dateStyle: 'medium', timeStyle: 'short' })
  const resends = status.resentAt?.length ? ` · ${toFaDigits(status.resentAt.length)} بار دوباره فرستاده شده` : ''
  return `فرستاده شد: ${when}${resends}`
}

/** The outcome of «ارسال» in words. */
export function emailOutcomeText(result) {
  if (!result) return ''
  if (result.outcome === 'ran') return 'ایمیل فرستاده شد.'
  if (result.outcome === 'already-done') return 'ایمیل این دوره قبلاً فرستاده شده بود؛ برای فرستادن دوباره «ارسال دوباره» را بزنید.'
  if (result.outcome === 'already-running') return 'همین حالا در حال فرستادن است؛ چند لحظه بعد وضعیت را ببینید.'
  if (result.outcome === 'idle' && result.summary?.reason === 'no_recipients') return 'گیرنده‌ای تنظیم نشده؛ از تب «تنظیمات» گیرندهٔ صورت‌حساب پیامک را وارد کنید. چیزی فرستاده نشد.'
  if (result.outcome === 'idle' && result.summary?.reason === 'mail_not_configured') return 'ارسال ایمیل روی سرور تنظیم نشده؛ چیزی فرستاده نشد.'
  return 'چیزی فرستاده نشد.'
}

/** The CSV's download name: `tooti-sms-1405-07.csv`. */
export function smsCsvFileName(key) {
  return `tooti-sms-${key}.csv`
}
